import React, { useState, useEffect } from "react";
import "./NPVCalculator.css"; // Import the stylesheet

const NPVCalculator = () => {
  const [baseAnnuityAmount, setBaseAnnuityAmount] = useState(16000);
  const [addition1, setAddition1] = useState(3600);
  const [addition2, setAddition2] = useState(360);
  const [deduction1, setDeduction1] = useState(4000);
  const [deduction2, setDeduction2] = useState(2000);
  const [annualBaseValue, setAnnualBaseValue] = useState(3600);
  const [widowAnnualBaseValue, setWidowAnnualBaseValue] = useState(2400);
  const [numberOfYears, setNumberOfYears] = useState(20);
  const [widowNumberOfYears, setWidowNumberOfYears] = useState(4);
  const [interestRate, setInterestRate] = useState(5);
  const [widowInterestRate, setWidowInterestRate] = useState(5);
  const [divider, setDivider] = useState(2);
  const [divider2, setDivider2] = useState(2);
  const [otherDeductions, setOtherDeductions] = useState(0); // New state for "Sonstige Abzüge"

  // State variables for result values
  const [totalAnnuity, setTotalAnnuity] = useState(0);
  const [annualAnnuityAmount, setAnnualAnnuityAmount] = useState(0);
  const [netGiftValue, setNetGiftValue] = useState(0);
  const [lifeAnnuity, setLifeAnnuity] = useState(0);
  const [widowLifeAnnuity, setWidowLifeAnnuity] = useState(0);
  const [capitalizedAnnuity, setCapitalizedAnnuity] = useState(0);
  const [divisionResult, setDivisionResult] = useState(0);
  const [divisionResult2, setDivisionResult2] = useState(0);

  useEffect(() => {
    calculateTotalAnnuity();
    calculateNPV();
  }, [
    baseAnnuityAmount,
    addition1,
    addition2,
    deduction1,
    deduction2,
    annualBaseValue,
    widowAnnualBaseValue,
    numberOfYears,
    widowNumberOfYears,
    interestRate,
    widowInterestRate,
    divider,
    divider2,
    otherDeductions, // Adding otherDeductions to the dependency array
  ]);

  useEffect(() => {
    // Calculate Capitalized Annuity and Division Result 2 after the initial render
    const totalAnnuityValue = totalAnnuity;
    const capitalizedValue = totalAnnuityValue * 20;
    setCapitalizedAnnuity(capitalizedValue);

    const divideBy2 = parseFloat(divider2);
    if (!isNaN(divideBy2) && divideBy2 !== 0) {
      const divisionResultValue = divisionResult;
      const divisionResult2Value = divisionResultValue / divideBy2;
      setDivisionResult2(divisionResult2Value);
    } else {
      setDivisionResult2(0);
    }
  }, [totalAnnuity, divider2, divisionResult]);

  const calculateTotalAnnuity = () => {
    const baseAmount = baseAnnuityAmount;
    const additionalAmountAsset = addition1 + addition2;
    const deductionAmountAsset = deduction1 + deduction2;
    const annuityAsset =
      baseAmount + additionalAmountAsset - deductionAmountAsset;

    setTotalAnnuity(annuityAsset);
  };

  const calculateNPV = () => {
    const baseAmount = baseAnnuityAmount;
    const years = numberOfYears;
    const rate = interestRate / 100;

    const widowBaseAmount = widowAnnualBaseValue;
    const widowYears = widowNumberOfYears;
    const widowRate = widowInterestRate / 100;

    const calculateLifeAnnuity = (Pmt, r, n) => {
      return (Pmt * (1 - Math.pow(1 + r, -n))) / r;
    };

    const lifeAnnuityValue = calculateLifeAnnuity(baseAmount, rate, years);
    const widowLifeAnnuityValue = calculateLifeAnnuity(
      widowBaseAmount,
      widowRate,
      widowYears
    );

    setLifeAnnuity(lifeAnnuityValue);
    setWidowLifeAnnuity(widowLifeAnnuityValue);

    const baseAmountAsset = baseAnnuityAmount;
    const additionalAmountAsset = addition1 + addition2;
    const deductionAmountAsset = deduction1 + deduction2;
    const annuityAsset =
      baseAmountAsset + additionalAmountAsset - deductionAmountAsset;

    const additionalAmountLiving = annualBaseValue;
    const annuityLiving = annualBaseValue;

    const widowAdditionalAmountLiving = widowAnnualBaseValue;
    const widowAnnuityLiving = widowAnnualBaseValue;

    const yearsAsset = numberOfYears;
    const widowYearsAsset = widowNumberOfYears;
    const rateAsset = interestRate / 100;
    const widowRateAsset = widowInterestRate / 100;

    let npv = 0;
    for (let i = 1; i <= yearsAsset; i++) {
      npv += annuityLiving / Math.pow(1 + rateAsset, i);
    }

    let widowNPV = 0;
    for (let i = 1; i <= widowYearsAsset; i++) {
      widowNPV += widowAnnuityLiving / Math.pow(1 + widowRateAsset, i);
    }

    const perpetualAnnuity = annuityAsset * 20;

    const totalAnnuityValue = totalAnnuity;
    setAnnualAnnuityAmount(perpetualAnnuity);
    setLifeAnnuity(npv);
    setWidowLifeAnnuity(widowNPV);

    const capitalizedValue = totalAnnuityValue * 20;

    setCapitalizedAnnuity(capitalizedValue);

    const netGift = perpetualAnnuity - npv - widowNPV;
    setNetGiftValue(netGift);

    const divideBy = parseFloat(divider);
    if (!isNaN(divideBy) && divideBy !== 0) {
      const divisionResultValue = netGift / divideBy;
      setDivisionResult(divisionResultValue);
    } else {
      setDivisionResult(0);
    }
  };

  return (
    <>
      <h2
        style={{
          marginLeft: "2rem",
          color: "#058785",
          marginTop: 0,
          paddingTop: "1rem",
        }}
      >
        Abfindungsrechner Landwirtschaft (Schätzung)
      </h2>
      <div style={{ marginLeft: "2rem" }}>
        Bei Anwendbarkeit des Anerbengesetzes (Ertrags- statt Verkehrswert).
        <p style={{ backgroundColor: "#FFF5EE" }}>
          Wesentlich ist, um welchen Zins der Hof pro Jahr verpachtet werden
          könnte.
        </p>
      </div>
      <div id="calculator">
        <div className="column">
          <h3 style={{ color: "#058785" }}>Ertragswert (Nettopachtzinsm.)</h3>
          <label htmlFor="baseAnnuityAmount">Pachtwert (zB 40ha*400€)</label>
          <input
            type="number"
            id="baseAnnuityAmount"
            value={baseAnnuityAmount}
            onChange={(e) => setBaseAnnuityAmount(parseFloat(e.target.value))}
            required
          />
          <label htmlFor="addition1">Mietwert Wohngebäude (zB 12M*300€)</label>
          <input
            type="number"
            id="addition1"
            value={addition1}
            onChange={(e) => setAddition1(parseFloat(e.target.value))}
            required
          />
          <label htmlFor="addition2">Waldzuwachs (zB 4ha*90€)</label>
          <input
            type="number"
            id="addition2"
            value={addition2}
            onChange={(e) => setAddition2(parseFloat(e.target.value))}
            required
          />
          <label htmlFor="deduction1">- Rep. an Gebäuden (zB 40ha*100€)</label>
          <input
            type="number"
            id="deduction1"
            value={deduction1}
            onChange={(e) => setDeduction1(parseFloat(e.target.value))}
            required
          />
          <label htmlFor="deduction2">
            - Feuerversicherg. & Grundsteuer (zB 2000€)
          </label>
          <input
            type="number"
            id="deduction2"
            value={deduction2}
            onChange={(e) => setDeduction2(parseFloat(e.target.value))}
            required
          />
          <div className="result">
            Jahresreinertrag: {totalAnnuity.toLocaleString("de-DE")}€
          </div>
          <div className="result capitalized-annuity">
            Kapitalisierter Reinertrag:{" "}
            {capitalizedAnnuity.toLocaleString("de-DE")}€
          </div>{" "}
          {/* Added Capitalized Annuity row */}
        </div>

        <div className="column">
          <h3 style={{ color: "#058785" }}>Übergeberpaar-Versorgung</h3>
          <label htmlFor="annualBaseValue">Wohnwert (zB 12M*300€) </label>
          <input
            type="number"
            id="annualBaseValue"
            value={annualBaseValue}
            onChange={(e) => setAnnualBaseValue(parseFloat(e.target.value))}
            required
          />

          <label htmlFor="numberOfYears">Stat. Bezugsdauer (Jahre) </label>
          <input
            type="number"
            id="numberOfYears"
            value={numberOfYears}
            onChange={(e) => setNumberOfYears(parseFloat(e.target.value))}
            required
          />

          <label htmlFor="interestRate">Kap.-Zins (%)</label>
          <input
            type="number"
            step="0.01"
            id="interestRate"
            value={interestRate}
            onChange={(e) => setInterestRate(parseFloat(e.target.value))}
            required
          />
          <div className="life-annuity">
            Paar-Rente: {lifeAnnuity.toLocaleString("de-DE").split(",")[0]}€
          </div>
          <h3 style={{ color: "#058785" }}>Einzelübergeber-Versorgung</h3>
          <label htmlFor="widowAnnualBaseValue">Wohnwert (zB 12M*200€)</label>
          <input
            type="number"
            id="widowAnnualBaseValue"
            value={widowAnnualBaseValue}
            onChange={(e) =>
              setWidowAnnualBaseValue(parseFloat(e.target.value))
            }
            required
          />

          <label htmlFor="widowNumberOfYears">Stat. Bezugsdauer (Jahre)</label>
          <input
            type="number"
            id="widowNumberOfYears"
            value={widowNumberOfYears}
            onChange={(e) => setWidowNumberOfYears(parseFloat(e.target.value))}
            required
          />

          <label htmlFor="widowInterestRate">Kap.-Zins (%)</label>
          <input
            type="number"
            step="0.01"
            id="widowInterestRate"
            value={widowInterestRate}
            onChange={(e) => setWidowInterestRate(parseFloat(e.target.value))}
            required
          />
          <div className="result">
            Einzelrente:{" "}
            {widowLifeAnnuity.toLocaleString("de-DE").split(",")[0]}€
          </div>
        </div>

        <div className="column">
          <h3 style={{ color: "#058785" }}>
            Geschenkwert & Abfindungsansprüche
          </h3>
          <div className="result">
            Geschenkwert Betrieb (Ertragswert-Versorgung):{" "}
            {netGiftValue.toLocaleString("de-DE").split(",")[0]}€
          </div>
          <div className="">
            Sonstige Abzüge (zB betriebliche Schulden; Zuschläge mit neg.
            Vorzeichen)
            <input
              type="number"
              value={otherDeductions}
              onChange={(e) => setOtherDeductions(parseFloat(e.target.value))}
            />
          </div>
          <div className="result">
            Kor. Geschenkwert:{" "}
            {
              (netGiftValue - otherDeductions)
                .toLocaleString("de-DE")
                .split(",")[0]
            }
            €
          </div>
          <label style={{ marginTop: "3rem" }} htmlFor="divider">
            Anzahl der Kinder
          </label>
          <input
            type="number"
            id="divider"
            value={divider}
            onChange={(e) => setDivider(parseFloat(e.target.value))}
            required
          />

          <div className="division-result">
            Erbteil je Kind:{" "}
            {
              ((netGiftValue - otherDeductions) / divider)
                .toLocaleString("de-DE")
                .split(",")[0]
            }
            €
          </div>
          <div className="divider2">Divider 2: {divider2}</div>
          <div className=" division-result">
            Pflichtteil je Kind:{" "}
            {
              ((netGiftValue - otherDeductions) / divider / 2)
                .toLocaleString("de-DE")
                .split(",")[0]
            }
            €
          </div>
          <div className=" division-result">
            Abfindungskosten:{" "}
            {
              (((netGiftValue - otherDeductions) / divider / 2) * (divider - 1))
                .toLocaleString("de-DE")
                .split(",")[0]
            }
            €
          </div>
        </div>
      </div>
    </>
  );
};

export default NPVCalculator;
